/**
 * UPI Payment Verification API
 * Handles UPI payment creation and status polling
 */

import axios from 'axios';

const POLLING_INTERVAL = 3000; // Poll every 3 seconds
const MAX_POLLING_TIME = 5 * 60 * 1000; // Max 5 minutes polling

/**
 * Create UPI payment request
 */
export const createUpiPaymentRequest = async (paymentData) => {
  try {
    const response = await axios.post('/api/upi-payment/create', paymentData);
    return response.data;
  } catch (error) {
    console.error('❌ Create UPI payment error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Check UPI payment status
 */
export const checkUpiPaymentStatus = async (transactionId) => {
  try {
    const response = await axios.post('/api/upi-payment/check-status', { transactionId });
    return response.data;
  } catch (error) {
    console.error('❌ Check UPI status error:', error);
    return {
      success: false,
      status: 'error',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Mark payment as success (manual verification)
 */
export const markUpiPaymentSuccess = async (transactionId, referenceNumber) => {
  try {
    const response = await axios.post('/api/upi-payment/mark-success', { 
      transactionId, 
      referenceNumber 
    });
    return response.data;
  } catch (error) {
    console.error('❌ Mark success error:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Start polling for payment status
 * @param {String} transactionId - Transaction ID to poll
 * @param {Function} onSuccess - Callback when payment succeeds
 * @param {Function} onFailed - Callback when payment fails
 * @param {Function} onTimeout - Callback when polling times out
 * @returns {Function} Stop polling function
 */
export const startPaymentPolling = (transactionId, onSuccess, onFailed, onTimeout) => {
  let isPolling = true;
  const startTime = Date.now();

  console.log('🔄 Starting UPI payment polling for:', transactionId);

  const poll = async () => {
    if (!isPolling) return;

    // Check if max polling time exceeded
    if (Date.now() - startTime > MAX_POLLING_TIME) {
      console.log('⏰ UPI payment polling timeout');
      onTimeout && onTimeout();
      return;
    }

    try {
      const result = await checkUpiPaymentStatus(transactionId);
      
      console.log('📡 UPI payment status:', result);

      if (result.status === 'success') {
        console.log('✅ UPI payment successful!');
        isPolling = false;
        onSuccess && onSuccess(result);
        return;
      } else if (result.status === 'failed' || result.status === 'declined') {
        console.log('❌ UPI payment failed');
        isPolling = false;
        onFailed && onFailed(result);
        return;
      }

      // Continue polling if still pending
      if (isPolling) {
        setTimeout(poll, POLLING_INTERVAL);
      }
    } catch (error) {
      console.error('❌ Polling error:', error);
      // Continue polling on error
      if (isPolling) {
        setTimeout(poll, POLLING_INTERVAL);
      }
    }
  };

  // Start polling
  poll();

  // Return stop function
  return () => {
    console.log('🛑 Stopping UPI payment polling');
    isPolling = false;
  };
};

export default {
  createUpiPaymentRequest,
  checkUpiPaymentStatus,
  markUpiPaymentSuccess,
  startPaymentPolling
};
